import React, { useRef, useState } from 'react';
import { Heart } from 'lucide-react';
import { useCart } from '../../../contexts/CartContext';
import { CART_CONSTANTS } from '../FuncationsCartAndWishlist/Dto/AddToCardtUtilityDto';
import './styles/ProductCard.Mobile.css';

interface ProductCardProps {
  id: string;
  title: string;
  price: string;
  image: string;
  slug: string;
  deliveryTag?: boolean;
  onAddToCart?: () => void;
  onMoreInfo?: () => void;
  onAddToWishlist?: () => void;
  onViewOptions?: () => void;
  isWishlisted?: boolean;
  isDragging?: boolean;
  quantity?: number;
  variants?: any[];
  hierarchicalVariants?: any[];
  variantHierarchy?: string[];
}

const ProductCardMobile: React.FC<ProductCardProps> = ({
  id,
  title,
  price,
  image,
  deliveryTag = true,
  onAddToCart,
  onMoreInfo,
  onAddToWishlist,
  onViewOptions,
  isWishlisted = false,
  isDragging = false,
  quantity,
  variants  
}) => {
  const touchStartRef = useRef<{x: number, y: number, time: number} | null>(null);
  const [touchMoved, setTouchMoved] = useState(false);
  const { cartItems, updateQuantity, removeFromCart } = useCart();

  const cartItem = cartItems.find(item => {
    if (item.productId !== id) return false;
    return !item.variantSku || item.variantSku === '';
  });

  const isInCart = !!cartItem;
  const cartQuantity = cartItem?.quantity || 0;

  // Handle touch start
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX, 
      y: e.touches[0].clientY,
      time: Date.now()
    };
    setTouchMoved(false);
  };

  // Handle touch move
  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartRef.current) {
      const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
      const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
      
      // If any movement more than 3px, consider it as scrolling
      if (deltaX > 3 || deltaY > 3) {
        setTouchMoved(true);
      }
    }
  };

  // Handle touch end - navigate on single tap
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartRef.current && onMoreInfo) {
      const deltaX = Math.abs(e.changedTouches[0].clientX - touchStartRef.current.x);
      const deltaY = Math.abs(e.changedTouches[0].clientY - touchStartRef.current.y);
      const timeDiff = Date.now() - touchStartRef.current.time;
      

      if (deltaX < 3 && deltaY < 3 && timeDiff < 200 && !isDragging && !touchMoved) {
        setTimeout(() => {
          if (onMoreInfo) {
            onMoreInfo();
          }
        }, 10);
      }
    }
    touchStartRef.current = null;
    setTouchMoved(false);
  };

  const handleWishlistClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToWishlist && !isDragging && !touchMoved) {
      onAddToWishlist();
    }
  };

  const handleAddToCartClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToCart && !isDragging && !touchMoved) {
      onAddToCart();
    }
  };

  const handleViewOptionsClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onViewOptions && !isDragging && !touchMoved) {
      onViewOptions();
    }
  };

  const handleCartQuantityChange = (newQuantity: number) => {
    if (!cartItem) return;

    if (newQuantity <= 0) {
      removeFromCart(cartItem.productId, cartItem.variantSku, cartItem.variantId);
    } else {
      updateQuantity(cartItem.productId, newQuantity, cartItem.variantSku, cartItem.variantId);
    }
  };

  const handleDecreaseClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleCartQuantityChange(cartQuantity - 1);
  };

  const handleIncreaseClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (cartQuantity < CART_CONSTANTS.MAX_QUANTITY) {
      handleCartQuantityChange(cartQuantity + 1);
    }
  };

  // Handle card click (for desktop)
  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    
    // Check if click is on button or interactive element
    const isInteractiveClick = target.closest('.wishlist-icon') || 
                               target.closest('.add-to-cart-btn') ||
                               target.tagName === 'BUTTON';
    
    // Only navigate if click is not on interactive elements and not dragging
    if (!isInteractiveClick && !isDragging) {
      if (onMoreInfo) {
        onMoreInfo();
      }
    }
  };

  // Assume quantity prop is available, fallback to 1 if not
  const outOfStock = typeof quantity === 'number' ? quantity === 0 : false;
  return (
    <div 
      className="product-card-mobile" 
      onClick={handleCardClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ 
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
        touchAction: 'auto',
        pointerEvents: 'auto',
        position: 'relative'
      }}
    >
      {/* Out of Stock Badge */}
      {outOfStock && (
        <div style={{position: 'absolute', top: 8, left: 8, background: '#ccc', color: '#fff', padding: '2px 8px', borderRadius: '8px', fontSize: '0.8rem', zIndex: 20}}>
          Out of Stock
        </div>
      )}
      {/* Wishlist Icon */}
      <div
        className="wishlist-icon"
        title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
        onClick={handleWishlistClick}
        onTouchEnd={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!touchMoved && !isDragging) {
            handleWishlistClick(e);
          }
        }}
        role="button"
        aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        style={{ 
          cursor: 'pointer', 
          position: 'absolute',
          top: '0.3rem',
          right: '0.3rem',
          zIndex: 10,
          WebkitTapHighlightColor: 'transparent',
          pointerEvents: 'auto'
        }}
      >
        <Heart 
          size={22} 
          color={isWishlisted ? '#e0245e' : '#000000ff'} 
          strokeWidth={2.2} 
          fill={isWishlisted ? '#e0245e' : 'none'} 
        />
      </div>

      {/* Delivery Tag */}
      {deliveryTag && (
        <div className="delivery-tag">
          30 min Delivery
        </div>
      )}
      
      {/* Product Image */}
      <div className="product-image-container-mobile">
        <img 
          src={image} 
          alt={title}
          className="product-image"
          draggable="false"
          style={{ pointerEvents: 'none' }}
        />
      </div>
      
      {/* Product Info */}
      <div className="product-info-mobile">
        <h3 className="product-title-mobile">{title}</h3>
        <div className="price-button-container-mobile">
          {!variants || variants.length === 0 ? <p className="product-price-mobile">{price}</p> : null}
          {/* Add to Cart / Quantity Controls / View Options */}
          {variants && variants.length > 0 ? (
            <button
              type="button"
              className="view-options-btn"
              onClick={handleViewOptionsClick}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!touchMoved && !isDragging) {
                  handleViewOptionsClick(e);
                }
              }}
              style={{ cursor: 'pointer', position: 'relative', zIndex: 10 }}
              aria-label="View options"
            >
              View Options
            </button>
          ) : isInCart ? (
            <div className="cart-quantity-controls">
              <button
                type="button"
                className="cart-quantity-btn"
                onClick={handleDecreaseClick as any}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!touchMoved && !isDragging) {
                    handleDecreaseClick(e);
                  }
                }}
                style={{ 
                  cursor: 'pointer', 
                  position: 'relative', 
                  zIndex: 10,
                  WebkitTapHighlightColor: 'transparent',
                  pointerEvents: 'auto'
                }}
                aria-label="Decrease quantity"
              >
                -
              </button>
              <span className="cart-quantity-display">{cartQuantity}</span>
              <button
                type="button"
                className="cart-quantity-btn"
                onClick={handleIncreaseClick as any}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!touchMoved && !isDragging) {
                    handleIncreaseClick(e);
                  }
                }}
                style={{ 
                  cursor: 'pointer', 
                  position: 'relative', 
                  zIndex: 10,
                  WebkitTapHighlightColor: 'transparent',
                  pointerEvents: 'auto'
                }}
                aria-label="Increase quantity"
                disabled={cartQuantity >= CART_CONSTANTS.MAX_QUANTITY}
              >
                +
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="add-to-cart-btn"
              onClick={handleAddToCartClick}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!touchMoved && !isDragging) {
                  handleAddToCartClick(e);
                }
              }}
              style={outOfStock ? { backgroundColor: '#ccc', color: '#888', cursor: 'not-allowed', position: 'relative', zIndex: 10 } : { cursor: 'pointer', position: 'relative', zIndex: 10 }}
              aria-label="Add to cart"
              disabled={quantity === 0}
            >
              Add
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCardMobile;